import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../utils/api";
import { useI18n } from "../contexts/I18nContext";

function timeAgo(dateStr) {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return "à l'instant";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}j`;
}

export default function Messages() {
  const { partnerId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useI18n();
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [content, setContent] = useState("");
  const [error, setError] = useState("");
  const [moderationWarning, setModerationWarning] = useState("");
  const [paywall, setPaywall] = useState(null);
  const messagesEndRef = useRef(null);

  // Fetch conversations list
  const fetchConversations = useCallback(async () => {
    try {
      const data = await api("/api/messages/conversations");
      setConversations(data.conversations || []);
    } catch (err) {
      console.error("Conversations error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch thread
  const fetchThread = useCallback(async () => {
    if (!partnerId) return;
    try {
      const data = await api(`/api/messages/${partnerId}`);
      setMessages(data.messages || []);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (err) {
      console.error("Thread error:", err);
    } finally {
      setLoading(false);
    }
  }, [partnerId]);

  useEffect(() => {
    setLoading(true);
    if (partnerId) {
      fetchThread();
      const interval = setInterval(fetchThread, 10000);
      return () => clearInterval(interval);
    } else {
      fetchConversations();
    }
  }, [partnerId, fetchThread, fetchConversations]);

  async function handleSend(e) {
    e.preventDefault();
    if (!content.trim() || sending) return;
    setSending(true);
    setError("");
    setModerationWarning("");

    try {
      const data = await api(`/api/messages/${partnerId}`, {
        method: "POST",
        body: JSON.stringify({ content: content.trim() }),
      });
      setMessages((prev) => [...prev, data.message]);
      setContent("");
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    } catch (err) {
      if (err.status === 403) {
        if (err.data?.paywall) {
          setPaywall(err.data);
        } else {
          setModerationWarning(err.data?.detail || "Message non conforme à la charte.");
        }
      } else {
        setError(err.message);
      }
    } finally {
      setSending(false);
    }
  }

  // ─── Conversation List ─────────────────────────────
  if (!partnerId) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="font-display text-2xl font-bold text-white">{t("messages_title")}</h2>
          <p className="mt-1 text-sm text-sensoria-text-dim">{t("messages_desc")}</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-sensoria-accent border-t-transparent" />
          </div>
        ) : conversations.length === 0 ? (
          <div className="py-16 text-center">
            <div className="mb-3 text-4xl">✉️</div>
            <p className="text-sensoria-text-dim">
              {t("messages_empty")}
              <br />
              Visitez l'annuaire pour envoyer un premier message.
            </p>
            <button
              onClick={() => navigate("/directory")}
              className="mt-4 text-sm font-medium text-sensoria-accent hover:text-sensoria-accent-light"
            >
              {t("messages_go_directory")}
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            {conversations.map((conv) => (
              <button
                key={conv.partner_id}
                onClick={() => navigate(`/messages/${conv.partner_id}`)}
                className="sensoria-card flex w-full items-center gap-3 text-left transition-all hover:sensoria-glow"
              >
                <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-sensoria-accent/15 text-xl">
                  {conv.partner?.avatar_emoji || "👤"}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-white truncate">
                      {conv.partner?.display_name || conv.partner?.username || "Membre"}
                    </span>
                    <span className="flex-shrink-0 text-[10px] text-sensoria-muted">
                      {timeAgo(conv.last_at)}
                    </span>
                  </div>
                  <p className="truncate text-xs text-sensoria-text-dim">{conv.last_message}</p>
                </div>
                {conv.unread > 0 && (
                  <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-rose-500 px-1.5 text-[10px] font-bold text-white">
                    {conv.unread}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ─── Chat Thread ───────────────────────────────────
  return (
    <div className="flex flex-col" style={{ minHeight: "calc(100vh - 180px)" }}>
      {/* Header */}
      <div className="mb-4 flex items-center gap-3">
        <button
          onClick={() => navigate("/messages")}
          className="rounded-lg p-2 text-sensoria-text-dim hover:bg-sensoria-surface hover:text-white transition-colors"
        >
          ←
        </button>
        <h2 className="font-display text-lg font-bold text-white">{t("messages_conversation")}</h2>
      </div>

      {/* Messages area */}
      <div className="flex-1 space-y-3 overflow-y-auto rounded-2xl border border-sensoria-border/30 bg-sensoria-surface/30 p-4">
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-sensoria-accent border-t-transparent" />
          </div>
        ) : messages.length === 0 ? (
          <div className="py-8 text-center">
            <p className="text-sm text-sensoria-text-dim">
              {t("messages_start")}
            </p>
          </div>
        ) : (
          messages.map((msg) => {
            const isMine = msg.sender_id === user?.id;
            return (
              <div
                key={msg.id}
                className={`flex ${isMine ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${
                    isMine
                      ? "rounded-br-md bg-sensoria-accent/20 text-sensoria-text"
                      : "rounded-bl-md bg-sensoria-card text-sensoria-text"
                  }`}
                >
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                  <p className={`mt-1 text-[10px] ${isMine ? "text-sensoria-accent/60" : "text-sensoria-muted"}`}>
                    {timeAgo(msg.created_at)}
                  </p>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Paywall modal */}
      {paywall && (
        <div className="mt-3 rounded-xl border border-sensoria-accent/30 bg-sensoria-card p-5 text-center animate-slide-up">
          <div className="mb-2 text-3xl">💜</div>
          <h3 className="font-display text-lg font-bold text-white">{paywall.detail}</h3>
          <p className="mt-1 text-xs text-sensoria-text-dim">
            {paywall.used}/{paywall.limit} conversations utilisées · Renouvellement dans {paywall.resets_in}
          </p>
          <button
            onClick={() => navigate("/premium")}
            className="sensoria-btn mt-3 px-6 py-2 text-sm"
          >
            Passer Premium — Messages illimités
          </button>
          <button
            onClick={() => setPaywall(null)}
            className="mt-2 block w-full text-xs text-sensoria-muted hover:text-white"
          >
            Fermer
          </button>
        </div>
      )}

      {/* Moderation warning */}
      {moderationWarning && (
        <div className="mt-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-300">
          ⚠️ {moderationWarning}
        </div>
      )}
      {error && (
        <div className="mt-2 rounded-lg border border-sensoria-danger/30 bg-sensoria-danger/10 px-3 py-2 text-xs text-sensoria-danger">
          {error}
        </div>
      )}

      {/* Input */}
      <form onSubmit={handleSend} className="mt-3 flex gap-2">
        <input
          type="text"
          value={content}
          onChange={(e) => { setContent(e.target.value); setModerationWarning(""); }}
          placeholder={t("messages_placeholder")}
          maxLength={2000}
          className="sensoria-input flex-1"
          autoFocus
        />
        <button
          type="submit"
          disabled={sending || !content.trim()}
          className="sensoria-btn px-5 py-3"
        >
          {sending ? (
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
          ) : (
            "→"
          )}
        </button>
      </form>
    </div>
  );
}
